import {
    type CSSProperties,
    useEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    type LayoutChangeEvent,
    useWindowDimensions,
    ScrollView,
    StyleSheet,
    View
} from "react-native";
import {
    CodeBlock
} from "./content";
import {
    SmartphoneKitIcon,
    ExternalKitIcon,
    MonitorKitIcon,
    TabletKitIcon,
    ReloadKitIcon
} from "./kitIcons";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    getCornerStyle,
    ToggleGroup,
    Resizable,
    Button,
    Tabs,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    useDocsLayout
} from "../theme";
import type {
    BlockEntry
} from "../blocks";
import {
    BLOCK_MESSAGE_SOURCE,
    type BlockSettings,
    type BlockViewport,
    BLOCK_SHARPNESSES,
    getBlockViewUrl,
    BLOCK_DENSITIES
} from "../blocks/settings";

type BlockFrameSettings = Omit<BlockSettings, "language">;

const BLOCK_DEVICE_WIDTHS: Record<Exclude<BlockViewport, "desktop">, number> = {
    tablet: 768,
    phone: 390
};

const stylesheet = StyleSheet.create({
    toolbar: {
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    heading: {
        flexDirection: "column",
        flexShrink: 1,
        flexGrow: 1
    },
    column: {
        flexDirection: "column"
    },
    settingsRow: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    setting: {
        flexDirection: "row",
        alignItems: "center"
    },
    actions: {
        flexDirection: "row",
        alignItems: "center"
    },
    code: {
        overflow: "hidden"
    }
});

const BlockFrame = ({
    isShowTitle = false,
    block
}: {
    isShowTitle?: boolean;
    block: BlockEntry;
}) => {
    const {
        language,
        t
    } = useDocsI18n();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        activeGapPropagation,
        activeSharpness,
        activePalette,
        shapeConfigs,
        activeTheme,
        paletteKeys,
        themeKeys,
        radiuses,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        height: windowHeight
    } = useWindowDimensions();

    const {
        headerHeight
    } = useDocsLayout();

    const [
        tab,
        setTab
    ] = useState("preview");

    const [
        viewport,
        setViewport
    ] = useState<BlockViewport>(block.viewport ?? "desktop");

    const [
        reloadKey,
        setReloadKey
    ] = useState(0);

    const [
        controlsHeight,
        setControlsHeight
    ] = useState(0);

    const [
        isResizing,
        setIsResizing
    ] = useState(false);

    const [
        settings,
        setSettings
    ] = useState<BlockFrameSettings>({
        themeKey: block.themeKey ?? activeTheme,
        gapPropagation: activeGapPropagation,
        sharpness: activeSharpness,
        paletteKey: activePalette
    });

    const orderedThemeKeys = [
        ...themeKeys
    ].sort((first, second) => Number(second === "light") - Number(first === "light"));

    const frameRef = useRef<HTMLIFrameElement>(null);

    const settingsRef = useRef(settings);

    const stageRef = useRef<View>(null);

    settingsRef.current = settings;

    const src = useMemo(() => getBlockViewUrl(block.id, {
        ...settingsRef.current,
        language
    }), [
        reloadKey,
        block.id,
        language
    ]);

    const postSettings = () => {
        frameRef.current?.contentWindow?.postMessage({
            settings: settingsRef.current,
            source: BLOCK_MESSAGE_SOURCE
        }, window.location.origin);
    };

    useEffect(() => {
        postSettings();
    }, [
        settings
    ]);

    useEffect(() => {
        const node = stageRef.current as unknown as HTMLElement | null;

        if(!node?.addEventListener) {
            return;
        }

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as HTMLElement | null;

            if(target?.closest?.("[role=\"separator\"]")) {
                setIsResizing(true);
            }
        };

        const handlePointerUp = () => {
            setIsResizing(false);
        };

        node.addEventListener("pointerdown", handlePointerDown, true);

        window.addEventListener("pointercancel", handlePointerUp);

        window.addEventListener("pointerup", handlePointerUp);

        return () => {
            node.removeEventListener("pointerdown", handlePointerDown, true);

            window.removeEventListener("pointercancel", handlePointerUp);

            window.removeEventListener("pointerup", handlePointerUp);
        };
    }, [
        tab
    ]);

    const updateSetting = (key: keyof BlockFrameSettings) => (value: string | Array<string>) => {
        if(typeof value !== "string" || !value) {
            return;
        }

        setSettings((current) => ({
            ...current,
            [key]: value
        }));
    };

    const handleOpenNewTab = () => {
        window.open(getBlockViewUrl(block.id, {
            ...settings,
            language
        }), "_blank", "noopener");
    };

    const handleReload = () => {
        setReloadKey((current) => current + 1);
    };

    const handleControlsLayout = (event: LayoutChangeEvent) => {
        if(tab !== "preview") {
            return;
        }

        setControlsHeight(event.nativeEvent.layout.height);
    };

    const frameHeight = Math.max(windowHeight - headerHeight - controlsHeight - spaces.spacingMd - spaces.spacingXxl, shapeConfigs.cardWidth);

    const frameStyle: CSSProperties = {
        pointerEvents: isResizing ? "none" : "auto",
        height: frameHeight,
        display: "block",
        width: "100%",
        border: 0
    };

    const renderSetting = (label: string, value: string, options: Array<{
        title: string;
        value: string;
    }>, key: keyof BlockFrameSettings) => {
        return <View
            style={[
                stylesheet.setting,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Text
                variant="labelMediumSize"
                color="mid"
            >
                {label}
            </Text>
            <ToggleGroup
                onValueChange={updateSetting(key)}
                attachBehaviour="attached"
                selectBehaviour="single"
                variant="outline"
                value={value}
                title={label}
                size="small"
            >
                {
                    options.map((option) => <ToggleGroup.Item
                        title={option.title}
                        value={option.value}
                        key={option.value}
                    />)
                }
            </ToggleGroup>
        </View>;
    };

    const renderPreviewControls = () => {
        if(tab !== "preview") {
            return null;
        }

        return <View
            style={[
                stylesheet.settingsRow,
                {
                    columnGap: spaces.spacingLg,
                    rowGap: spaces.spacingSm
                }
            ]}
        >
            {renderSetting(t("blocks.theme"), settings.themeKey, orderedThemeKeys.map((themeKey) => ({
                title: t(`blocks.theme.${themeKey}`),
                value: themeKey
            })), "themeKey")}
            {renderSetting(t("blocks.palette"), settings.paletteKey, paletteKeys.map((paletteKey) => ({
                title: localize(`palette-switcher-${paletteKey}` as Parameters<typeof localize>[0]),
                value: paletteKey
            })), "paletteKey")}
            {renderSetting(t("blocks.density"), settings.gapPropagation, BLOCK_DENSITIES.map((density) => ({
                title: t(`blocks.density.${density}`),
                value: density
            })), "gapPropagation")}
            {renderSetting(t("blocks.sharpness"), settings.sharpness, BLOCK_SHARPNESSES.map((sharpness) => ({
                title: t(`blocks.sharpness.${sharpness}`),
                value: sharpness
            })), "sharpness")}
        </View>;
    };

    const renderStage = () => {
        if(tab === "code") {
            return <ScrollView
                style={[
                    stylesheet.code,
                    getCornerStyle({
                        radius: radiuses.surface,
                        shapeConfigs
                    }),
                    {
                        height: frameHeight
                    }
                ]}
            >
                <CodeBlock
                    code={block.source}
                    language="tsx"
                />
            </ScrollView>;
        }

        return <Card
            spreadBehaviour="stretch"
            variant="inset"
        >
            <Card.Content
                isFlush={true}
            >
                <Resizable.PanelGroup
                    orientation="horizontal"
                    style={{
                        height: frameHeight
                    }}
                    compactBreakpoint={0}
                    persistKey={null}
                    key={viewport}
                >
                    <Resizable.Panel
                        defaultSize={viewport === "desktop" ? 100 : `${BLOCK_DEVICE_WIDTHS[viewport]}px`}
                        minSize={`${BLOCK_DEVICE_WIDTHS.phone}px`}
                        title={t(block.titleKey)}
                    >
                        <iframe
                            title={t("blocks.frameTitle", [
                                t(block.titleKey)
                            ])}
                            onLoad={postSettings}
                            style={frameStyle}
                            key={reloadKey}
                            loading="lazy"
                            ref={frameRef}
                            src={src}
                        />
                    </Resizable.Panel>
                    <Resizable.Handle
                        title={t("blocks.resize")}
                        isGripVisible={true}
                    />
                    <Resizable.Panel
                        defaultSize={viewport === "desktop" ? 0 : undefined}
                        title={t("blocks.resizeSpace")}
                        minSize={0}
                    />
                </Resizable.PanelGroup>
            </Card.Content>
        </Card>;
    };

    return <View
        style={[
            stylesheet.column,
            {
                gap: spaces.spacingMd
            }
        ]}
    >
        <View
            onLayout={handleControlsLayout}
            style={[
                stylesheet.column,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            {
                isShowTitle ?
                    <View
                        style={[
                            stylesheet.heading,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Text
                            variant="titleMediumSize"
                            color="high"
                        >
                            {t(block.titleKey)}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t(block.leadKey)}
                        </Text>
                    </View>
                :
                    null
            }
            <View
                style={[
                    stylesheet.toolbar,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Tabs
                    onValueChange={setTab}
                    variant="segment"
                    size="small"
                    value={tab}
                >
                    <Tabs.List
                        title={t("blocks.mode")}
                    >
                        <Tabs.Trigger
                            title={t("blocks.preview")}
                            value="preview"
                        />
                        <Tabs.Trigger
                            title={t("blocks.code")}
                            value="code"
                        />
                    </Tabs.List>
                </Tabs>
                <View
                    style={[
                        stylesheet.actions,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <ToggleGroup
                        onValueChange={(value) => typeof value === "string" && value ? setViewport(value as BlockViewport) : undefined}
                        isDisabled={tab !== "preview"}
                        title={t("blocks.viewport")}
                        attachBehaviour="attached"
                        selectBehaviour="single"
                        variant="outline"
                        value={viewport}
                        size="small"
                    >
                        <ToggleGroup.Item
                            title={t("blocks.desktop")}
                            icon={MonitorKitIcon}
                            isIconOnly={true}
                            value="desktop"
                        />
                        <ToggleGroup.Item
                            title={t("blocks.tablet")}
                            icon={TabletKitIcon}
                            isIconOnly={true}
                            value="tablet"
                        />
                        <ToggleGroup.Item
                            title={t("blocks.phone")}
                            icon={SmartphoneKitIcon}
                            isIconOnly={true}
                            value="phone"
                        />
                    </ToggleGroup>
                    <Button
                        accessibilityLabel={t("blocks.reload")}
                        isDisabled={tab !== "preview"}
                        onPress={handleReload}
                        spreadBehaviour="free"
                        icon={ReloadKitIcon}
                        variant="outline"
                        type="neutral"
                        size="small"
                    />
                    <Button
                        title={t("blocks.openNewTab")}
                        onPress={handleOpenNewTab}
                        spreadBehaviour="free"
                        icon={ExternalKitIcon}
                        iconDirection="end"
                        variant="outline"
                        type="neutral"
                        size="small"
                    />
                </View>
            </View>
            {renderPreviewControls()}
        </View>
        <View
            ref={stageRef}
        >
            {renderStage()}
        </View>
    </View>;
};
export default BlockFrame;
